import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/layout/Header';
import ErrorBanner from '../../components/ui/ErrorBanner';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import RoleBadge from '../../components/ui/RoleBadge';
import apiClient from '../../api/apiClient';
import { formatDateTime } from '../../utils/formatters';

const ROLES = ['admin','manager','viewer'];

function InviteModal({ onClose }) {
  const [form, setForm] = useState({ email: '', role: 'viewer' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);

  const handle = async (e) => {
    e.preventDefault(); setLoading(true); setErr('');
    try {
      const res = await apiClient.post('/auth/invite', form);
      setResult(res.data);
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to generate invite');
    } finally { setLoading(false); }
  };

  const copy = () => {
    navigator.clipboard.writeText(result.inviteUrl).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700' }}>Invite New User</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.25rem', color: '#94a3b8' }}>×</button>
        </div>
        {err && <div style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: '0.75rem' }}>{err}</div>}
        {!result ? (
          <form onSubmit={handle} style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
            <div>
              <label style={styles.label}>Email *</label>
              <input type="email" required value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} style={styles.input} placeholder="user@example.com" />
            </div>
            <div>
              <label style={styles.label}>Role</label>
              <select value={form.role} onChange={e => setForm(f => ({...f, role: e.target.value}))} style={styles.input}>
                {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase()+r.slice(1)}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button type="button" onClick={onClose} style={styles.cancelBtn}>Cancel</button>
              <button type="submit" disabled={loading} style={styles.submitBtn}>{loading ? 'Sending…' : 'Send Invite'}</button>
            </div>
          </form>
        ) : (
          <div>
            <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: '#374151' }}>
              {result.emailSent
                ? <>✅ Invite emailed to <strong>{result.email}</strong>. Expires in 7 days.</>
                : <>⚠️ Couldn't send the email automatically — share this link with <strong>{result.email}</strong> manually. Expires in 7 days.</>}
            </p>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '0.75rem', wordBreak: 'break-all', fontSize: '0.8rem', fontFamily: 'monospace', marginBottom: '0.75rem' }}>
              {result.inviteUrl}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={copy} style={styles.submitBtn}>{copied ? '✅ Copied!' : '📋 Copy Link'}</button>
              <button onClick={onClose} style={styles.cancelBtn}>Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Access Panel for a single user ────────────────────────────────────────────
function UserAccessPanel({ userId }) {
  const [accounts, setAccounts] = useState([]);
  const [buAssignments, setBuAssignments] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);
  const [allBus, setAllBus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selAccount, setSelAccount] = useState('');
  const [selRole, setSelRole] = useState('viewer');
  const [selBu, setSelBu] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [accRes, buRes, allAccRes, buListRes] = await Promise.allSettled([
        apiClient.get(`/users/${userId}/accounts`),
        apiClient.get(`/users/${userId}/business-units`),
        apiClient.get('/accounts'),
        apiClient.get('/business-units'),
      ]);
      if (accRes.status === 'fulfilled') setAccounts(accRes.value.data.accounts || []);
      if (buRes.status === 'fulfilled') setBuAssignments(buRes.value.data.assignments || []);
      if (allAccRes.status === 'fulfilled') setAllAccounts(allAccRes.value.data.accounts || allAccRes.value.data || []);
      if (buListRes.status === 'fulfilled') setAllBus(buListRes.value.data.units || []);
    } catch {}
    finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const grantAccount = async () => {
    if (!selAccount) return;
    await apiClient.post(`/users/${userId}/accounts`, { account_id: Number(selAccount), role: selRole });
    setSelAccount(''); await load();
  };

  const revokeAccount = async (accountId) => {
    await apiClient.delete(`/users/${userId}/accounts/${accountId}`);
    await load();
  };

  const assignBu = async () => {
    if (!selBu) return;
    const buObj = allBus.find(b => b.id === Number(selBu));
    await apiClient.post(`/users/${userId}/business-units`, { business_unit_id: Number(selBu), account_id: buObj?.account_id });
    setSelBu(''); await load();
  };

  const removeBu = async (buId) => {
    await apiClient.delete(`/users/${userId}/business-units/${buId}`);
    await load();
  };

  if (loading) return <div style={{ padding: '0.75rem', fontSize: '0.8rem', color: '#94a3b8' }}>Loading…</div>;

  const assignedBuIds = new Set(buAssignments.map(b => b.business_unit_id));
  const availableBus = allBus.filter(b => !assignedBuIds.has(b.id));

  return (
    <div style={{ padding: '0.875rem 1rem', background: '#f8fafc', borderTop: '1px solid #f1f5f9', display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
      {/* Business Access */}
      <div style={{ flex: '1 1 240px' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#374151', marginBottom: '0.5rem' }}>Brand Access</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginBottom: '0.5rem' }}>
          {accounts.map(acc => (
            <div key={acc.id} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', background: '#ede9fe', color: '#6366f1', padding: '0.2rem 0.5rem 0.2rem 0.625rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600' }}>
              <span>{acc.name}</span>
              {acc.isHome ? <span style={{ fontSize: '0.65rem', color: '#a78bfa' }}>(home)</span> : (
                <button onClick={() => revokeAccount(acc.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: '0.9rem', lineHeight: 1, padding: 0, fontWeight: '700' }}>×</button>
              )}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
          <select value={selAccount} onChange={e => setSelAccount(e.target.value)} style={panelStyles.select}>
            <option value="">+ Add brand…</option>
            {allAccounts.filter(a => !accounts.find(ac => ac.id === a.id)).map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
          {selAccount && (
            <>
              <select value={selRole} onChange={e => setSelRole(e.target.value)} style={{ ...panelStyles.select, width: '90px' }}>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <button onClick={grantAccount} style={panelStyles.btn}>Grant</button>
            </>
          )}
        </div>
      </div>

      {/* Business Unit Access */}
      <div style={{ flex: '1 1 240px' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#374151', marginBottom: '0.5rem' }}>Business Unit Access</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginBottom: '0.5rem' }}>
          {buAssignments.map(b => (
            <div key={b.business_unit_id} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', background: '#ecfdf5', color: '#059669', padding: '0.2rem 0.5rem 0.2rem 0.625rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600' }}>
              <span>{b.name}</span>
              <button onClick={() => removeBu(b.business_unit_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#059669', fontSize: '0.9rem', lineHeight: 1, padding: 0, fontWeight: '700' }}>×</button>
            </div>
          ))}
          {buAssignments.length === 0 && <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>All units (no restriction)</span>}
        </div>
        <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
          <select value={selBu} onChange={e => setSelBu(e.target.value)} style={panelStyles.select}>
            <option value="">+ Restrict to unit…</option>
            {availableBus.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          {selBu && <button onClick={assignBu} style={panelStyles.btn}>Assign</button>}
        </div>
      </div>
    </div>
  );
}

const panelStyles = {
  select: { padding: '0.3rem 0.5rem', border: '1px solid #e2e8f0', borderRadius: '0.375rem', fontSize: '0.75rem', outline: 'none', background: '#fff', maxWidth: '160px' },
  btn: { padding: '0.3rem 0.625rem', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600' },
};

function UserForm({ onSubmit, onCancel, initial }) {
  const [form, setForm] = useState(initial || { name:'', email:'', password:'', role:'viewer' });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handle = async (e) => {
    e.preventDefault(); setSubmitting(true); setErr('');
    try { await onSubmit(form); }
    catch (e) { setErr(e.response?.data?.error || 'Failed'); setSubmitting(false); }
  };

  return (
    <form onSubmit={handle} style={styles.form}>
      {err && <div style={{ color:'#dc2626', fontSize:'0.8rem', marginBottom:'0.5rem' }}>{err}</div>}
      <div style={styles.formRow}>
        <div style={styles.ff}>
          <label style={styles.label}>Name</label>
          <input value={form.name} onChange={e=>set('name',e.target.value)} style={styles.input} placeholder="Full name" />
        </div>
        <div style={styles.ff}>
          <label style={styles.label}>Email *</label>
          <input type="email" required value={form.email} onChange={e=>set('email',e.target.value)} style={styles.input} placeholder="user@example.com" />
        </div>
      </div>
      <div style={styles.formRow}>
        <div style={styles.ff}>
          <label style={styles.label}>{initial ? 'New Password (leave blank to keep)' : 'Password *'}</label>
          <input type="password" required={!initial} value={form.password} onChange={e=>set('password',e.target.value)} style={styles.input} placeholder="••••••••" />
        </div>
        <div style={styles.ff}>
          <label style={styles.label}>Role *</label>
          <select value={form.role} onChange={e=>set('role',e.target.value)} style={styles.input}>
            {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase()+r.slice(1)}</option>)}
          </select>
        </div>
      </div>
      <div style={{ display:'flex', gap:'0.5rem', justifyContent:'flex-end' }}>
        {onCancel && <button type="button" onClick={onCancel} style={styles.cancelBtn}>Cancel</button>}
        <button type="submit" disabled={submitting} style={styles.submitBtn}>{submitting ? 'Saving…' : initial ? 'Update User' : 'Create User'}</button>
      </div>
    </form>
  );
}

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [pendingResets, setPendingResets] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [copiedReset, setCopiedReset] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [resendingInvite, setResendingInvite] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, resetsRes, invitesRes] = await Promise.allSettled([
        apiClient.get('/users'),
        apiClient.get('/auth/pending-resets'),
        apiClient.get('/auth/pending-invites'),
      ]);
      if (usersRes.status === 'fulfilled') setUsers(usersRes.value.data.users || []);
      else setError(usersRes.reason?.response?.data?.error || 'Failed to load users');
      if (resetsRes.status === 'fulfilled') setPendingResets(resetsRes.value.data.resets || []);
      if (invitesRes.status === 'fulfilled') setPendingInvites(invitesRes.value.data.invites || []);
    } finally { setLoading(false); }
  }, []);

  const resendInvite = async (id) => {
    setResendingInvite(id);
    try { await apiClient.post(`/auth/invite/${id}/resend`); await load(); }
    finally { setResendingInvite(null); }
  };

  const cancelInvite = async (id) => {
    if (!window.confirm('Cancel this invite?')) return;
    await apiClient.delete(`/auth/invite/${id}`);
    await load();
  };

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (form) => {
    await apiClient.post('/users', form);
    setShowForm(false); await load();
  };

  const handleUpdate = async (form) => {
    await apiClient.put(`/users/${editing.id}`, form);
    setEditing(null); await load();
  };

  const handleToggle = async (user) => {
    await apiClient.put(`/users/${user.id}`, { active: user.active ? 0 : 1 });
    await load();
  };

  const copyReset = (url, id) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedReset(id); setTimeout(() => setCopiedReset(null), 2000);
    });
  };

  const toggleExpand = (userId) => {
    setExpandedUser(prev => prev === userId ? null : userId);
  };

  return (
    <div>
      <Header title="Users" />
      <div style={styles.content}>
        <ErrorBanner message={error} />
        {showInvite && <InviteModal onClose={() => { setShowInvite(false); load(); }} />}

        {pendingInvites.length > 0 && (
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '0.75rem', padding: '1rem 1.25rem', marginBottom: '1rem' }}>
            <h4 style={{ margin: '0 0 0.625rem 0', fontSize: '0.875rem', fontWeight: '700', color: '#1e40af' }}>📧 Pending Invites ({pendingInvites.length})</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
              {pendingInvites.map(inv => {
                const expired = inv.expires_at && new Date(inv.expires_at) < new Date();
                return (
                <div key={inv.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.8rem', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: '600', color: '#374151', minWidth: '160px' }}>{inv.email}</span>
                  <RoleBadge role={inv.role} />
                  <span style={{ color: '#64748b' }}>{inv.account_name}</span>
                  <span style={{ color: '#94a3b8' }}>Invited by {inv.invited_by || '—'}</span>
                  {expired ? (
                    <span style={{ color: '#dc2626', fontWeight: '700' }}>Expired {inv.expires_at?.slice(0, 10)}</span>
                  ) : (
                    <span style={{ color: '#94a3b8' }}>Expires: {inv.expires_at?.slice(0, 10)}</span>
                  )}
                  <button onClick={() => resendInvite(inv.id)} disabled={resendingInvite === inv.id} style={{ ...styles.actionBtn, color: '#6366f1', border: '1px solid #e2e8f0', padding: '0.2rem 0.5rem', borderRadius: '0.375rem', background: '#f8fafc' }}>
                    {resendingInvite === inv.id ? 'Resending…' : '📨 Resend'}
                  </button>
                  <button onClick={() => cancelInvite(inv.id)} style={{ ...styles.actionBtn, color: '#dc2626', border: '1px solid #e2e8f0', padding: '0.2rem 0.5rem', borderRadius: '0.375rem', background: '#f8fafc' }}>
                    Cancel
                  </button>
                </div>
                );
              })}
            </div>
          </div>
        )}

        {pendingResets.length > 0 && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '0.75rem', padding: '1rem 1.25rem', marginBottom: '1rem' }}>
            <h4 style={{ margin: '0 0 0.625rem 0', fontSize: '0.875rem', fontWeight: '700', color: '#92400e' }}>⏳ Pending Password Resets ({pendingResets.length})</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
              {pendingResets.map(r => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.8rem', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: '600', color: '#374151', minWidth: '160px' }}>{r.email}</span>
                  <span style={{ color: '#64748b', fontFamily: 'monospace', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '320px' }}>{r.resetUrl}</span>
                  <button onClick={() => copyReset(r.resetUrl, r.id)} style={{ ...styles.actionBtn, color: '#6366f1', border: '1px solid #e2e8f0', padding: '0.2rem 0.5rem', borderRadius: '0.375rem', background: '#f8fafc' }}>
                    {copiedReset === r.id ? '✅ Copied' : '📋 Copy'}
                  </button>
                  <span style={{ color: '#94a3b8' }}>Expires: {r.expires_at?.slice(0, 10)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {editing ? (
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Edit User: {editing.name || editing.email}</h3>
            <UserForm initial={editing} onSubmit={handleUpdate} onCancel={() => setEditing(null)} />
          </div>
        ) : (
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>Users ({users.length})</h3>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={() => setShowInvite(true)} style={{ ...styles.addBtn, background: '#10b981' }}>📧 Invite User</button>
                <button onClick={() => setShowForm(v => !v)} style={styles.addBtn}>
                  {showForm ? 'Cancel' : '+ New User'}
                </button>
              </div>
            </div>

            {showForm && (
              <div style={{ marginBottom: '1.25rem', padding: '1rem', background: '#f8fafc', borderRadius: '0.625rem' }}>
                <UserForm onSubmit={handleCreate} onCancel={() => setShowForm(false)} />
              </div>
            )}

            {loading ? <LoadingSpinner /> : (
              <div style={{ overflowX: 'auto' }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      {['Name','Email','Home Account','Role','Status','Created','Actions'].map(h => <th key={h} style={styles.th}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 ? (
                      <tr><td colSpan={7} style={styles.empty}>No users</td></tr>
                    ) : users.map((u, i) => (
                      <React.Fragment key={u.id}>
                        <tr style={i % 2 === 0 ? {} : { background: '#f8fafc' }}>
                          <td style={styles.td}>{u.name || '—'}</td>
                          <td style={styles.td}>{u.email}</td>
                          <td style={styles.td}>{u.account_name || '—'}</td>
                          <td style={styles.td}><RoleBadge role={u.role} /></td>
                          <td style={styles.td}>
                            <span style={{ color: u.active ? '#22c55e' : '#94a3b8', fontWeight: '600', fontSize: '0.8rem' }}>
                              {u.active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td style={styles.td}>{formatDateTime(u.created_at)}</td>
                          <td style={styles.td}>
                            <button onClick={() => setEditing(u)} style={styles.actionBtn}>Edit</button>
                            <button onClick={() => handleToggle(u)} style={{ ...styles.actionBtn, color: u.active ? '#f59e0b' : '#22c55e' }}>
                              {u.active ? 'Deactivate' : 'Activate'}
                            </button>
                            <button onClick={() => toggleExpand(u.id)} style={{ ...styles.actionBtn, color: '#64748b' }}>
                              {expandedUser === u.id ? '▲ Access' : '▼ Access'}
                            </button>
                          </td>
                        </tr>
                        {expandedUser === u.id && (
                          <tr>
                            <td colSpan={7} style={{ padding: 0 }}>
                              <UserAccessPanel userId={u.id} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  content: { padding: '1.5rem 1.75rem' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' },
  modal: { background: '#fff', borderRadius: '0.875rem', padding: '1.5rem', width: '100%', maxWidth: '460px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  card: { background: '#fff', borderRadius: '0.75rem', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  cardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' },
  cardTitle: { margin: '0 0 1rem 0', fontSize: '0.95rem', fontWeight: '700', color: '#1e293b' },
  addBtn: { padding: '0.4rem 0.875rem', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600' },
  form: { display: 'flex', flexDirection: 'column', gap: '0.875rem' },
  formRow: { display: 'flex', gap: '0.875rem', flexWrap: 'wrap' },
  ff: { display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: 1, minWidth: '200px' },
  label: { fontSize: '0.8rem', fontWeight: '600', color: '#374151' },
  input: { padding: '0.5rem 0.75rem', border: '1.5px solid #e2e8f0', borderRadius: '0.5rem', fontSize: '0.9rem', outline: 'none' },
  submitBtn: { padding: '0.5rem 1.25rem', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600' },
  cancelBtn: { padding: '0.5rem 1rem', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600', color: '#475569' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' },
  th: { padding: '0.625rem 0.75rem', background: '#f8fafc', color: '#64748b', fontWeight: '600', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'left', borderBottom: '1px solid #e2e8f0' },
  td: { padding: '0.625rem 0.75rem', borderBottom: '1px solid #f1f5f9', color: '#1e293b', verticalAlign: 'middle' },
  actionBtn: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: '0.8rem', fontWeight: '600', padding: '0 0.375rem' },
  empty: { padding: '2rem', textAlign: 'center', color: '#94a3b8' },
};
